import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../../lib/api';
import { fetchJson } from '../../../lib/apiFetchJson';
import { FavoriteCountsResponseSchema, favoriteTargetId, isCountsEntity, type FavoriteEntity, type FavoriteItem } from '@shared/flow/signal-chips';

export const FAVORITE_COUNTS_QUERY_KEY = ['flow-favorite-counts'] as const;

/**
 * `items` is the favorites configuration the counts are for. Each favorite contributes its id AND its target, because
 * the response is looked up by favorite id: a changed favorite (a new id, or a new target under the same id) is a
 * different query, never a reused count that no longer matches the tile.
 */
export function favoriteCountsQueryKey(entity: FavoriteEntity, viewAs: string | null, items: ReadonlyArray<FavoriteItem>) {
  return [...FAVORITE_COUNTS_QUERY_KEY, entity, viewAs ?? null, items.map(item => `${item.id}:${favoriteTargetId(item)}`)] as const;
}

function countsUrl(entity: FavoriteEntity, viewAs: string | null): string {
  const base = `/api/flow/favorites/counts?entity=${encodeURIComponent(entity)}`;
  return viewAs ? `${base}&viewAs=${encodeURIComponent(viewAs)}` : base;
}

/**
 * Per-favorite counts for one tab's favorites configuration (`items`). Plays favorites never show a count, so
 * `entity: 'play'` stays idle (no request) whatever `enabled` says. Cached indefinitely; invalidated by
 * `invalidateSignalQueries`, the list mutations that change Companies/People rows, and the favorite / view
 * mutations (prefix match on the root). No focus refetch: a configuration change arrives through the favorites
 * query and selects a new key. Errors render as "–" inline, so the global error toast is suppressed.
 */
export function useFavoriteCounts(
  entity: FavoriteEntity,
  viewAs: string | null,
  options: { enabled: boolean; items: ReadonlyArray<FavoriteItem> },
) {
  const { apiFetch } = useApi();
  return useQuery({
    enabled: options.enabled && isCountsEntity(entity),
    queryKey: favoriteCountsQueryKey(entity, viewAs, options.items),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    meta: { suppressErrorToast: true },
    queryFn: () => fetchJson(apiFetch, FavoriteCountsResponseSchema, countsUrl(entity, viewAs)),
  });
}
